/* ManufacturingSpecForm.jsx — Planner-authored MFG spec, surfaced as a
 * dedicated tab on PlannerCaseDetail when the case is in the
 * `approved_pending_mfg` window (and later, read-only).
 *
 * Sections (all planner-owned; production-owned sections like
 * thermoforming temperature / packaging are deferred to Phase 2):
 *   01 Delivery information
 *   02 Aligner material
 *   03 Retainer material
 *   04 Aligner specifications (counts read-only from plan + planner extras)
 *   05 Planner notes & handoff
 *   06 Pre-submission checklist  →  Submit to Admin
 *
 * Editing rules:
 *   - Editable only when case.status === "approved_pending_mfg".
 *   - Once submitted (status flips to `awaiting_production_assignment`),
 *     this same component renders read-only.
 *
 * Style:
 *   - Reuses PlanSectionAccordion (matches the Author-the-plan tab rhythm)
 *   - Theme tokens only (emerald primary, amber for in-progress, slate
 *     neutrals). No left color stripes.
 */
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PlanSectionAccordion from "@/components/case/PlanSectionAccordion";
import UnlockRequestControl from "@/components/planner/UnlockRequestControl";
import WorkspaceSection from "@/components/planner/WorkspaceSection";
import {
    Package, Wrench, Sparkle, ClipboardText, CheckCircle, PaperPlaneRight,
    CircleNotch, Lock, FloppyDisk, Warning,
} from "@phosphor-icons/react";


/* ─────────────────────────────────────────────────────────────────────
 * Options
 * ───────────────────────────────────────────────────────────────────── */

const ALIGNER_MATERIALS = [
    { id: "tpu_premium_05",  label: "TPU Premium 0.5mm",   sub: "High-clarity dual-layer base" },
    { id: "tpu_standard_07", label: "TPU Standard 0.7mm",  sub: "Workhorse single-layer" },
    { id: "petg_075",        label: "PETG 0.75mm",         sub: "Budget alternative" },
    { id: "tpu_soft_04",     label: "TPU Soft 0.4mm",      sub: "Comfort-first, light forces" },
    { id: "custom",          label: "Custom",              sub: "Specify below" },
];

const HARDNESS = [
    { id: "high",   label: "High (hTPU)",   sub: "Strong force delivery" },
    { id: "medium", label: "Medium (mTPU)", sub: "Balanced — default" },
    { id: "soft",   label: "Soft (sTPU)",   sub: "Light force, comfort" },
];

const LAYER_CONFIG = [
    { id: "single", label: "Single Layer" },
    { id: "dual",   label: "Dual Layer" },
    { id: "triple", label: "Triple Layer" },
];

/* Aligner thickness presets — common dental-thermoforming gauges. */
const ALIGNER_THICKNESS = [
    { id: "0.4",  label: "0.4 mm" },
    { id: "0.5",  label: "0.5 mm" },
    { id: "0.6",  label: "0.6 mm" },
    { id: "0.7",  label: "0.7 mm" },
    { id: "0.75", label: "0.75 mm" },
    { id: "0.8",  label: "0.8 mm" },
];

/* Retainer is locked to Invisique Infinite per product requirement.
   Planner only picks the gauge. */
const RETAINER_MATERIAL_DEFAULT = "invisique_infinite";
const RETAINER_THICKNESS = [
    { id: "0.75", label: "0.75 mm" },
    { id: "0.8",  label: "0.8 mm" },
    { id: "1.0",  label: "1.0 mm" },
    { id: "1.5",  label: "1.5 mm" },
];

const CHECKLIST = [
    "Doctor has signed off on the clinical plan",
    "Aligner sheet material + hardness + thickness selected",
    "Layer configuration matches force-delivery plan",
    "Retainer material + quantity reflects the tier deliverable",
    "Treatment rationale + critical success factors documented",
    "Known risks / edge cases noted for the production team",
];


/* Simple chip-selector primitive — keeps the radio-list visual rhythm
   consistent across material / hardness / layer pickers. */
function ChipPicker({ value, onChange, options, columns = 2, disabled, testId }) {
    return (
        <div
            className={`grid gap-2 ${columns === 3 ? "grid-cols-1 sm:grid-cols-3" : "grid-cols-1 sm:grid-cols-2"}`}
            data-testid={testId}
        >
            {options.map((o) => {
                const selected = value === o.id;
                return (
                    <button
                        key={o.id}
                        type="button"
                        disabled={disabled}
                        onClick={() => !disabled && onChange(o.id)}
                        data-testid={`${testId}-${o.id}`}
                        className={`text-left rounded-xl border px-3 py-2.5 transition-all ${
                            selected
                                ? "border-emerald-500 ring-2 ring-emerald-500/20 bg-gradient-to-br from-emerald-50 to-emerald-100/40"
                                : "border-border bg-card hover:border-emerald-300"
                        } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
                    >
                        <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                                <div className="font-semibold text-[13px] leading-tight">{o.label}</div>
                                {o.sub && (
                                    <div className="text-[11px] text-muted-foreground mt-0.5">{o.sub}</div>
                                )}
                            </div>
                            {selected && (
                                <CheckCircle size={16} weight="fill" className="text-emerald-600 shrink-0 mt-0.5" />
                            )}
                        </div>
                    </button>
                );
            })}
        </div>
    );
}


/* Field label + small required asterisk + optional helper subtitle. */
function FieldLabel({ children, required, hint }) {
    return (
        <div className="mb-1.5">
            <Label className="text-[10px] font-bold uppercase tracking-[0.16em] text-emerald-700">
                {children}
                {required && <span className="text-rose-600 ml-1">*</span>}
            </Label>
            {hint && <div className="text-[11px] text-muted-foreground mt-0.5">{hint}</div>}
        </div>
    );
}


/* ─────────────────────────────────────────────────────────────────────
 * Main component
 * ───────────────────────────────────────────────────────────────────── */

/* Client-side mirror of `_mfg_required_fields_missing` in the backend
   (`planner_cases.py`). Kept in sync manually — replicated so the tab
   body renders instantly without a network round-trip. Fields:
     · aligner_material         Aligner sheet material
     · aligner_hardness         Material hardness
     · aligner_thickness_mm     Aligner thickness
     · layer_config             Layer configuration
     · retainer_thickness_mm    Retainer thickness
*/
function computeMissingRequired(spec) {
    const s = spec || {};
    const missing = [];
    if (!(s.aligner_material || "").trim())       missing.push("Aligner sheet material");
    if (!(s.aligner_hardness || "").trim())        missing.push("Material hardness");
    if (!s.aligner_thickness_mm)                   missing.push("Aligner thickness");
    if (!(s.layer_config || "").trim())            missing.push("Layer configuration");
    if (!s.retainer_thickness_mm)                  missing.push("Retainer thickness");
    return missing;
}


export default function ManufacturingSpecForm({ caseId, c, onSubmitted }) {
    // iter101.49 — Admin can grant a temporary re-edit via the unlock
    // request flow. When `case.mfg_unlocked_at` is stamped, the form
    // becomes editable again regardless of status — and re-submit
    // clears the flag so the block re-locks automatically.
    const mfgUnlocked = !!c?.mfg_unlocked_at;
    const editable = c?.status === "approved_pending_mfg" || mfgUnlocked;
    const submitted = !editable && (
        c?.status === "awaiting_production_assignment"
        || c?.status === "in_production"
        || c?.status === "shipped"
        || c?.status === "in_treatment"
        || c?.status === "treatment_started"
        || c?.status === "refinement_requested"
        || c?.status === "completed"
    );

    /* iter101.58 — Seed state synchronously from the case object so the
       tab renders instantly. The prior implementation fired a GET
       /planner/cases/{id}/manufacturing-spec on mount, adding a
       200–500ms flash of "Loading manufacturing spec…" before the
       form appeared. `c.manufacturing_spec` is already hydrated on
       the case fetch — reuse it. `missing_required` is computed
       client-side via the mirror above.

       iter101.94 — Also pre-fill `required_delivery_date` from the
       case's own `target_completion_date` when the planner opens
       the form for the first time. This keeps the case SLA target
       and the planner MFG delivery date in lockstep by default;
       the field is rendered read-only in the UI and any planner
       ask for more time is captured via `extension_note`. */
    const initialSpec = useMemo(() => {
        const loaded = { ...(c?.manufacturing_spec || {}) };
        if (!loaded.retainer_material) {
            loaded.retainer_material = RETAINER_MATERIAL_DEFAULT;
        }
        if (!loaded.required_delivery_date && c?.target_completion_date) {
            loaded.required_delivery_date = String(c.target_completion_date).slice(0, 10);
        }
        return loaded;
    }, [c?.manufacturing_spec, c?.target_completion_date]);

    const [spec, setSpec] = useState(initialSpec);
    const [missing, setMissing] = useState(() => computeMissingRequired(initialSpec));
    const [saveState, setSaveState] = useState({ status: "idle", at: null });
    const [submitting, setSubmitting] = useState(false);
    // iter101.49 — When the case is locked, all accordions start
    // CLOSED so the planner sees a calm "handed off" surface rather
    // than a scroll of open forms. They can still expand any section
    // for reference — the individual `toggle` fn handles that. When
    // the case is editable, we default-open Section 01 as before.
    const [openSection, setOpenSection] = useState(submitted ? null : "01");
    const debounceRef = useRef(null);

    /* Counts read-only from the locked plan — single source of truth. */
    const planCounts = useMemo(() => {
        const p = c?.plan || {};
        const up = Number(p.aligner_count_upper || 0);
        const lo = Number(p.aligner_count_lower || 0);
        const total = Math.max(up, lo);
        const sheets = up + lo;
        return { up, lo, total, sheets };
    }, [c?.plan]);

    /* iter101.58 — Redundant GET removed. State is seeded from
       `c.manufacturing_spec` synchronously above; if the parent case
       object gets refetched (e.g. after unlock), the useMemo above
       gives us a fresh initialSpec but state has already diverged,
       so we resync only when the case id changes. */
    useEffect(() => {
        setSpec(initialSpec);
        setMissing(computeMissingRequired(initialSpec));
    }, [caseId, initialSpec]);

    /* Debounced autosave — only when editable. Server still returns
       its own missing_required after each PUT (authoritative) but we
       don't block on it — the local mirror keeps the UI snappy. */
    const persist = useCallback(async (next) => {
        if (!editable) return;
        setSaveState({ status: "saving", at: null });
        try {
            const r = await api.put(`/planner/cases/${caseId}/manufacturing-spec`, next);
            setSpec(r.data?.spec || next);
            setMissing(r.data?.missing_required || computeMissingRequired(next));
            setSaveState({ status: "saved", at: new Date() });
        } catch (err) {
            setSaveState({ status: "error", at: null });
            const msg = err.response?.data?.detail || "Couldn't save MFG spec";
            toast.error(msg);
        }
    }, [caseId, editable]);

    const upd = useCallback((patch) => {
        setSpec((prev) => {
            const next = { ...(prev || {}), ...patch };
            if (debounceRef.current) clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => persist(next), 700);
            return next;
        });
    }, [persist]);

    /* Toggle one accordion section open / closed. */
    const toggle = (id) => setOpenSection((cur) => (cur === id ? "" : id));

    /* Submit — final gate. */
    const handleSubmit = async () => {
        if (submitting) return;
        setSubmitting(true);
        try {
            // Flush any pending debounced save first.
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
                debounceRef.current = null;
                await persist(spec);
            }
            const r = await api.post(`/planner/cases/${caseId}/submit-mfg-spec`);
            toast.success("MFG spec submitted — admin will assign a production tech.");
            onSubmitted?.(r.data);
        } catch (err) {
            const msg = err.response?.data?.detail || "Submission failed";
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    /* iter101.58 — Loading guard removed; state is seeded synchronously
       from `c.manufacturing_spec` so `spec` is never null. */

    /* Section completion helpers — drive the green check in the headers. */
    const sec01Complete = !!(spec.required_delivery_date || spec.special_handling_notes || spec.extension_note);
    const sec02Complete = !!(spec.aligner_material && spec.aligner_hardness && spec.aligner_thickness_mm);
    const sec03Complete = !!spec.retainer_thickness_mm;
    const sec04Complete = !!(spec.incremental_movement_mm || spec.stage_notes);
    const sec05Complete = !!(spec.treatment_rationale && spec.critical_success_factors);
    const sec06Complete = !!spec.checklist_acknowledged;

    const allRequiredOk = missing.length === 0;
    const canSubmit = editable && allRequiredOk && spec.checklist_acknowledged && !submitting;

    /* iter101.57 — Header + body merged into a single WorkspaceSection
       so the MFG spec surface shares the exact same outer container
       language as the Doctor's Submission tab (one rounded-2xl card
       containing header strip + all accordions inside). Previously
       the header was a separate card floating above the accordions
       — user flagged that the MFG accordions weren't "in a container
       like the other 2". */
    const mfgSubhead = editable
        ? "Doctor approved. Finalise the MFG spec before sending to Admin."
        : submitted
            ? "Locked — submitted to Admin for production assignment."
            : "Becomes editable after the doctor approves the plan.";
    const mfgHeaderRight = (
        <>
            {editable && <SaveIndicator state={saveState} />}
            {submitted && (
                <>
                    <span
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-100 border border-slate-200 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-700"
                        data-testid="mfg-spec-locked-pill"
                    >
                        <Lock size={11} weight="fill" /> Locked
                    </span>
                    {/* iter101.49 — Planner can raise a formal
                        unlock request when they spot an issue
                        post-submit. Admin resolves from the
                        admin case detail page. */}
                    <UnlockRequestControl
                        caseId={caseId}
                        section="mfg_spec"
                        caseObj={c}
                        onChanged={onSubmitted}
                    />
                </>
            )}
        </>
    );

    return (
        <WorkspaceSection
            title="Manufacturing Specification"
            subhead={mfgSubhead}
            right={mfgHeaderRight}
            testid="planner-mfg-spec"
        >
            <div className="space-y-4">
                {!editable && !submitted && (
                    <div className="rounded-2xl border border-amber-200 bg-amber-50/60 px-4 py-3 text-[12px] text-amber-900">
                        The MFG spec opens for editing the moment the doctor approves the clinical plan. Until then, this tab is preview-only.
                    </div>
                )}

            {/* 01 — Delivery Information */}
            <PlanSectionAccordion
                anchorId="mfg-sec-01"
                number={1}
                label="Delivery"
                summary={sec01Complete ? "Required date or handling notes captured" : "Optional — required date + handling"}
                isOpen={openSection === "01"}
                isComplete={sec01Complete}
                onToggle={() => toggle("01")}
                onNext={() => setOpenSection("02")}
                testIdPrefix="mfg-sec-01"
            >
                <DeliverySection
                    c={c}
                    spec={spec}
                    editable={editable}
                    upd={upd}
                />
            </PlanSectionAccordion>

            {/* 02 — Aligner Material */}
            <PlanSectionAccordion
                anchorId="mfg-sec-02"
                number={2}
                label="Aligner material"
                summary={
                    sec02Complete
                        ? `${ALIGNER_MATERIALS.find((m) => m.id === spec.aligner_material)?.label || "—"} · ${spec.aligner_thickness_mm}mm · ${HARDNESS.find((h) => h.id === spec.aligner_hardness)?.label || "—"}`
                        : "Required — sheet, hardness, thickness"
                }
                isOpen={openSection === "02"}
                isComplete={sec02Complete}
                onToggle={() => toggle("02")}
                onNext={() => setOpenSection("03")}
                testIdPrefix="mfg-sec-02"
            >
                <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <FieldLabel required>Sheet material</FieldLabel>
                            <Select
                                value={spec.aligner_material || ""}
                                onValueChange={(v) => upd({ aligner_material: v })}
                                disabled={!editable}
                            >
                                <SelectTrigger data-testid="mfg-aligner-material-trigger">
                                    <SelectValue placeholder="Select sheet material" />
                                </SelectTrigger>
                                <SelectContent>
                                    {ALIGNER_MATERIALS.map((m) => (
                                        <SelectItem
                                            key={m.id}
                                            value={m.id}
                                            data-testid={`mfg-aligner-material-${m.id}`}
                                        >
                                            <div className="flex flex-col">
                                                <span className="font-medium">{m.label}</span>
                                                {m.sub && <span className="text-[11px] text-muted-foreground">{m.sub}</span>}
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div>
                            <FieldLabel required>Hardness</FieldLabel>
                            <Select
                                value={spec.aligner_hardness || ""}
                                onValueChange={(v) => upd({ aligner_hardness: v })}
                                disabled={!editable}
                            >
                                <SelectTrigger data-testid="mfg-aligner-hardness-trigger">
                                    <SelectValue placeholder="Select hardness" />
                                </SelectTrigger>
                                <SelectContent>
                                    {HARDNESS.map((h) => (
                                        <SelectItem
                                            key={h.id}
                                            value={h.id}
                                            data-testid={`mfg-aligner-hardness-${h.id}`}
                                        >
                                            <div className="flex flex-col">
                                                <span className="font-medium">{h.label}</span>
                                                {h.sub && <span className="text-[11px] text-muted-foreground">{h.sub}</span>}
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {spec.aligner_material === "custom" && (
                        <div>
                            <FieldLabel>Custom material details</FieldLabel>
                            <Input
                                value={spec.aligner_material_custom || ""}
                                onChange={(e) => upd({ aligner_material_custom: e.target.value })}
                                disabled={!editable}
                                placeholder="e.g. ZenSplint 0.6mm, lot batch ABC123"
                                data-testid="mfg-aligner-material-custom"
                            />
                        </div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <FieldLabel required>Thickness</FieldLabel>
                            <Select
                                value={spec.aligner_thickness_mm != null ? String(spec.aligner_thickness_mm) : ""}
                                onValueChange={(v) => upd({ aligner_thickness_mm: v === "" ? null : Number(v) })}
                                disabled={!editable}
                            >
                                <SelectTrigger data-testid="mfg-aligner-thickness-trigger">
                                    <SelectValue placeholder="Select thickness" />
                                </SelectTrigger>
                                <SelectContent>
                                    {ALIGNER_THICKNESS.map((t) => (
                                        <SelectItem
                                            key={t.id}
                                            value={t.id}
                                            data-testid={`mfg-aligner-thickness-${t.id}`}
                                        >
                                            {t.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <FieldLabel>Layer configuration</FieldLabel>
                            <Select
                                value={spec.layer_config || ""}
                                onValueChange={(v) => upd({ layer_config: v })}
                                disabled={!editable}
                            >
                                <SelectTrigger data-testid="mfg-layer-config-trigger">
                                    <SelectValue placeholder="Select layer config" />
                                </SelectTrigger>
                                <SelectContent>
                                    {LAYER_CONFIG.map((l) => (
                                        <SelectItem
                                            key={l.id}
                                            value={l.id}
                                            data-testid={`mfg-layer-config-${l.id}`}
                                        >
                                            {l.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {(spec.layer_config === "dual" || spec.layer_config === "triple") && (
                        <div>
                            <FieldLabel hint="Optional — describe layer order, soft-between-hard sandwich, etc.">
                                Layer configuration details
                            </FieldLabel>
                            <Textarea
                                rows={2}
                                value={spec.layer_config_details || ""}
                                onChange={(e) => upd({ layer_config_details: e.target.value })}
                                disabled={!editable}
                                placeholder="e.g. Soft TPU 0.3mm between two hard 0.6mm layers."
                                data-testid="mfg-layer-details"
                                className="resize-none"
                            />
                        </div>
                    )}
                </div>
            </PlanSectionAccordion>

            {/* 03 — Retainer Material (locked to Invisique™ Infinite) */}
            <PlanSectionAccordion
                anchorId="mfg-sec-03"
                number={3}
                label="Retainer material"
                summary={
                    sec03Complete
                        ? `Invisique™ Infinite · ${spec.retainer_thickness_mm}mm${spec.retainer_quantity ? ` · ${spec.retainer_quantity} set${spec.retainer_quantity === 1 ? "" : "s"}` : ""}`
                        : "Required — pick thickness"
                }
                isOpen={openSection === "03"}
                isComplete={sec03Complete}
                onToggle={() => toggle("03")}
                onNext={() => setOpenSection("04")}
                testIdPrefix="mfg-sec-03"
            >
                <div className="space-y-4">
                    {/* Locked default — Invisique™ Infinite */}
                    <div>
                        <FieldLabel>Retainer material</FieldLabel>
                        <div
                            className="flex items-center justify-between gap-3 rounded-xl border border-emerald-200 bg-emerald-50/60 px-3.5 py-2.5"
                            data-testid="mfg-retainer-material-locked"
                        >
                            <div className="flex items-center gap-2">
                                <CheckCircle size={16} weight="fill" className="text-emerald-600" />
                                <div>
                                    <div className="font-semibold text-[13px] text-emerald-900">Invisique™ Infinite</div>
                                    <div className="text-[11px] text-emerald-800/70">Standard retainer material across all CoE-planned cases.</div>
                                </div>
                            </div>
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-600/10 border border-emerald-300 text-[9px] font-bold uppercase tracking-[0.14em] text-emerald-700">
                                <Lock size={9} weight="fill" /> Default
                            </span>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <FieldLabel required>Thickness</FieldLabel>
                            <Select
                                value={spec.retainer_thickness_mm != null ? String(spec.retainer_thickness_mm) : ""}
                                onValueChange={(v) => upd({ retainer_thickness_mm: v === "" ? null : Number(v) })}
                                disabled={!editable}
                            >
                                <SelectTrigger data-testid="mfg-retainer-thickness-trigger">
                                    <SelectValue placeholder="Select thickness" />
                                </SelectTrigger>
                                <SelectContent>
                                    {RETAINER_THICKNESS.map((t) => (
                                        <SelectItem
                                            key={t.id}
                                            value={t.id}
                                            data-testid={`mfg-retainer-thickness-${t.id}`}
                                        >
                                            {t.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <FieldLabel hint="Defaults to the included count from the treatment tier.">
                                Retainer quantity (sets)
                            </FieldLabel>
                            <Input
                                type="number"
                                min="1"
                                max="10"
                                value={spec.retainer_quantity ?? ""}
                                onChange={(e) => upd({ retainer_quantity: e.target.value === "" ? null : Number(e.target.value) })}
                                disabled={!editable}
                                placeholder="2"
                                data-testid="mfg-retainer-quantity"
                            />
                        </div>
                    </div>
                </div>
            </PlanSectionAccordion>

            {/* 04 — Aligner Specifications */}
            <PlanSectionAccordion
                anchorId="mfg-sec-04"
                number={4}
                label="Aligner specifications"
                summary={`${planCounts.up} U / ${planCounts.lo} L · ${planCounts.sheets} sheets`}
                isOpen={openSection === "04"}
                isComplete={sec04Complete}
                onToggle={() => toggle("04")}
                onNext={() => setOpenSection("05")}
                testIdPrefix="mfg-sec-04"
            >
                <div className="space-y-4">
                    {/* Read-only counts pulled from the locked plan */}
                    <div className="rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3">
                        <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-700 mb-2">
                            From the approved plan (read-only)
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            <ReadOnlyStat label="Upper aligners" value={planCounts.up || "—"} />
                            <ReadOnlyStat label="Lower aligners" value={planCounts.lo || "—"} />
                            <ReadOnlyStat label="Total stages" value={planCounts.total || "—"} />
                            <ReadOnlyStat label="Aligner sheets" value={planCounts.sheets || "—"} />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <FieldLabel hint="Optional — default rate per stage; planner can override per-stage in notes below.">
                                Incremental movement / stage (mm)
                            </FieldLabel>
                            <Input
                                type="number"
                                step="0.05"
                                min="0.05"
                                max="1"
                                value={spec.incremental_movement_mm ?? ""}
                                onChange={(e) => upd({ incremental_movement_mm: e.target.value === "" ? null : Number(e.target.value) })}
                                disabled={!editable}
                                placeholder="0.25"
                                data-testid="mfg-incremental-movement"
                            />
                        </div>
                    </div>

                    <div>
                        <FieldLabel hint="Stage-specific overrides not already captured in the per-tooth plan.">
                            Custom stage notes
                        </FieldLabel>
                        <Textarea
                            rows={3}
                            value={spec.stage_notes || ""}
                            onChange={(e) => upd({ stage_notes: e.target.value })}
                            disabled={!editable}
                            placeholder="e.g. Stage 6: enable IPR 0.3mm 31-32. Stage 10-12: activate bite ramps."
                            data-testid="mfg-stage-notes"
                            className="resize-none"
                        />
                    </div>
                </div>
            </PlanSectionAccordion>

            {/* 05 — Planner Notes & Handoff */}
            <PlanSectionAccordion
                anchorId="mfg-sec-05"
                number={5}
                label="Planner notes & handoff"
                summary={
                    sec05Complete
                        ? "Rationale + critical factors captured"
                        : "Rationale, critical factors, risks"
                }
                isOpen={openSection === "05"}
                isComplete={sec05Complete}
                onToggle={() => toggle("05")}
                onNext={() => setOpenSection("06")}
                testIdPrefix="mfg-sec-05"
            >
                <div className="space-y-4">
                    <div>
                        <FieldLabel hint="Why this sequence, materials, and force-delivery strategy.">
                            Treatment rationale
                        </FieldLabel>
                        <Textarea
                            rows={3}
                            value={spec.treatment_rationale || ""}
                            onChange={(e) => upd({ treatment_rationale: e.target.value })}
                            disabled={!editable}
                            placeholder="Explain the design choices the production team should be aware of."
                            data-testid="mfg-treatment-rationale"
                            className="resize-none"
                        />
                    </div>
                    <div>
                        <FieldLabel hint="Patient compliance gates, timing dependencies, etc.">
                            Critical success factors
                        </FieldLabel>
                        <Textarea
                            rows={3}
                            value={spec.critical_success_factors || ""}
                            onChange={(e) => upd({ critical_success_factors: e.target.value })}
                            disabled={!editable}
                            placeholder="e.g. Elastics must be engaged from stage 9 — patient briefed."
                            data-testid="mfg-critical-success-factors"
                            className="resize-none"
                        />
                    </div>
                    <div>
                        <FieldLabel hint="Anything the production team should watch for.">
                            Known risks / edge cases
                        </FieldLabel>
                        <Textarea
                            rows={3}
                            value={spec.known_risks || ""}
                            onChange={(e) => upd({ known_risks: e.target.value })}
                            disabled={!editable}
                            placeholder="e.g. Patient has history of aligner breakage — use extra-durable batch if possible."
                            data-testid="mfg-known-risks"
                            className="resize-none"
                        />
                    </div>
                </div>
            </PlanSectionAccordion>

            {/* 06 — Pre-submission checklist + submit CTA */}
            <PlanSectionAccordion
                anchorId="mfg-sec-06"
                number={6}
                label="Submit to admin"
                summary={
                    /* iter101.58 — When the spec has already been
                       submitted, the summary reads as an authored
                       stamp ("Submitted to admin by X · date") instead
                       of the misleading "Ready to submit". */
                    submitted && spec.submitted_at
                        ? `Submitted to admin by ${spec.submitted_by_name || "Planner"} · ${new Date(spec.submitted_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}`
                        : sec06Complete && allRequiredOk
                            ? "Ready to submit"
                            : missing.length > 0
                                ? `${missing.length} required field${missing.length === 1 ? "" : "s"} pending`
                                : "Acknowledge checklist to enable submit"
                }
                isOpen={openSection === "06"}
                isComplete={(submitted && spec.submitted_at) || (sec06Complete && allRequiredOk)}
                onToggle={() => toggle("06")}
                hideNext
                testIdPrefix="mfg-sec-06"
            >
                <div className="space-y-4">
                    {missing.length > 0 && (
                        <div
                            className="rounded-xl border border-rose-200 bg-rose-50/60 px-4 py-3 text-[12px] text-rose-900"
                            data-testid="mfg-spec-missing-banner"
                        >
                            <div className="font-bold mb-1">Required before submit:</div>
                            <ul className="list-disc list-inside space-y-0.5">
                                {missing.map((m) => <li key={m}>{m}</li>)}
                            </ul>
                        </div>
                    )}

                    <div className="rounded-xl border border-border bg-card px-4 py-3">
                        <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-emerald-700 mb-3">
                            Pre-submission checklist
                        </div>
                        <ul className="space-y-2 text-[13px]">
                            {CHECKLIST.map((item) => (
                                <li key={item} className="flex items-start gap-2">
                                    <CheckCircle size={14} weight="fill" className="text-emerald-600 shrink-0 mt-0.5" />
                                    <span className="text-foreground/80">{item}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <label className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50/40 px-4 py-3 cursor-pointer">
                        <Checkbox
                            checked={!!spec.checklist_acknowledged}
                            onCheckedChange={(checked) => upd({ checklist_acknowledged: !!checked })}
                            disabled={!editable}
                            data-testid="mfg-checklist-ack"
                            className="mt-0.5"
                        />
                        <div className="text-[13px] leading-snug">
                            <span className="font-semibold text-emerald-900">I confirm the MFG spec is complete.</span>
                            <span className="text-emerald-800/80"> All required materials, retainer details, and planner notes are captured for the production team.</span>
                        </div>
                    </label>

                    <div className="flex items-center justify-between gap-3 flex-wrap pt-1">
                        <div className="text-[11px] text-muted-foreground italic">
                            Submitting moves this case from <span className="font-semibold text-foreground">MFG Spec Pending</span> → <span className="font-semibold text-foreground">Awaiting Production</span>. Admin will assign a production technician.
                        </div>
                        <Button
                            onClick={handleSubmit}
                            disabled={!canSubmit}
                            data-testid="mfg-submit-to-admin"
                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        >
                            {submitting
                                ? (<><CircleNotch size={14} className="animate-spin mr-1.5" />Submitting…</>)
                                : (<><PaperPlaneRight size={14} weight="fill" className="mr-1.5" />Submit to Admin</>)}
                        </Button>
                    </div>
                </div>
            </PlanSectionAccordion>

            {/* iter101.58 — "Submitted to admin" banner removed;
                the info now lives inside the section-06 summary
                stamp (see PlanSectionAccordion above). */}
            </div>
        </WorkspaceSection>
    );
}


/* ─────────────────────────────────────────────────────────────────────
 * DeliverySection — read-only case SLA target + extension request
 *
 * iter101.94 — The MFG `required_delivery_date` mirrors the case's
 * `target_completion_date` (the promise made to the doctor at plan
 * approval). Rendered as a read-only display so admin remains the
 * single owner of the delivery promise. If the planner realistically
 * needs more MFG time, they open the "Request extension" panel and
 * write a rationale — captured on the MFG spec as `extension_note`
 * which admin reads before deciding whether to nudge the target date.
 * ───────────────────────────────────────────────────────────────────── */
function DeliverySection({ c, spec, editable, upd }) {
    const [extensionOpen, setExtensionOpen] = useState(
        !!(spec?.extension_note && spec.extension_note.trim())
    );
    const caseTarget = c?.target_completion_date
        ? String(c.target_completion_date).slice(0, 10)
        : null;
    const priorityLabel = c?.pricing?.priority_label
        || (c?.pricing?.priority_code ? c.pricing.priority_code.replace(/_/g, " ") : null);
    const slaDays = c?.target_completion_sla_days ?? c?.pricing?.priority_tat_days;
    const sourceLabel = c?.target_completion_source === "manual" ? "Admin-set" : "Auto · SLA";

    const fmtDate = (iso) => {
        if (!iso) return "—";
        try {
            const d = new Date(iso + "T00:00:00");
            return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
        } catch { return iso; }
    };

    return (
        <div className="space-y-4">
            {/* Read-only Required delivery date — mirrors case SLA target */}
            <div>
                <FieldLabel hint="Owned by Admin — reflects the case SLA target promised at plan approval.">
                    Required delivery date
                </FieldLabel>
                <div
                    className="flex items-center justify-between gap-3 rounded-xl border border-emerald-200 bg-emerald-50/60 px-3.5 py-2.5"
                    data-testid="mfg-required-delivery-date-readonly"
                >
                    <div className="flex items-center gap-2 min-w-0">
                        <Lock size={14} weight="fill" className="text-emerald-700 shrink-0" />
                        <div className="min-w-0">
                            <div
                                className="font-semibold text-[14px] text-emerald-900"
                                data-testid="mfg-required-delivery-date-value"
                            >
                                {fmtDate(spec.required_delivery_date || caseTarget)}
                            </div>
                            <div className="text-[11px] text-emerald-800/70">
                                {caseTarget
                                    ? `Case SLA target · ${sourceLabel}${slaDays ? ` · ${slaDays}d TAT` : ""}${priorityLabel ? ` · ${priorityLabel}` : ""}`
                                    : "No SLA target set yet — admin will stamp on approval."}
                            </div>
                        </div>
                    </div>
                    {caseTarget && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-600/10 border border-emerald-300 text-[9px] font-bold uppercase tracking-[0.14em] text-emerald-700 shrink-0">
                            <CheckCircle size={9} weight="fill" /> Locked
                        </span>
                    )}
                </div>

                {/* Request extension trigger */}
                <div className="mt-2 flex items-center justify-between gap-3 flex-wrap">
                    <div className="text-[11px] text-muted-foreground italic">
                        Need extra manufacturing time? Send admin a note — the target date only changes if admin approves.
                    </div>
                    {editable && !extensionOpen && (
                        <button
                            type="button"
                            onClick={() => setExtensionOpen(true)}
                            className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.08em] px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 hover:bg-amber-200 transition"
                            data-testid="mfg-request-extension-btn"
                        >
                            <Warning size={11} weight="fill" /> Request extension
                        </button>
                    )}
                </div>

                {(extensionOpen || (spec.extension_note && spec.extension_note.trim())) && (
                    <div
                        className="mt-3 rounded-xl border border-amber-200 bg-amber-50/60 px-3.5 py-3"
                        data-testid="mfg-extension-panel"
                    >
                        <div className="flex items-center justify-between gap-2 mb-2">
                            <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-amber-800">
                                <Warning size={11} weight="fill" /> Extension request
                            </div>
                            {editable && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        upd({ extension_note: "" });
                                        setExtensionOpen(false);
                                    }}
                                    className="text-[10px] font-bold uppercase tracking-[0.08em] text-muted-foreground hover:text-foreground"
                                    data-testid="mfg-extension-clear-btn"
                                >
                                    Clear
                                </button>
                            )}
                        </div>
                        <Textarea
                            rows={3}
                            value={spec.extension_note || ""}
                            onChange={(e) => upd({ extension_note: e.target.value })}
                            disabled={!editable}
                            placeholder="e.g. IPR schedule needs extra staging — 3 additional working days would land a safer plan."
                            data-testid="mfg-extension-note"
                            className="resize-none bg-white"
                        />
                        <div className="text-[10.5px] text-amber-900/70 mt-1.5 leading-snug">
                            Admin reviews this note and, if approved, updates the case target date. The delivery date above updates automatically once admin adjusts the SLA.
                        </div>
                    </div>
                )}
            </div>

            {/* Special handling notes */}
            <div>
                <FieldLabel hint="Fragile handling, temperature-sensitive routing, etc.">Special handling notes</FieldLabel>
                <Textarea
                    rows={3}
                    value={spec.special_handling_notes || ""}
                    onChange={(e) => upd({ special_handling_notes: e.target.value })}
                    disabled={!editable}
                    placeholder="e.g. Signature required on delivery."
                    data-testid="mfg-special-handling"
                    className="resize-none"
                />
            </div>
        </div>
    );
}


function ReadOnlyStat({ label, value }) {
    return (
        <div>
            <div className="text-[9px] font-bold uppercase tracking-[0.14em] text-muted-foreground">{label}</div>
            <div className="font-mono text-sm font-bold mt-0.5">{value}</div>
        </div>
    );
}function SaveIndicator({ state }) {
    if (state.status === "saving") {
        return (
            <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
                <CircleNotch size={12} className="animate-spin" /> Saving…
            </span>
        );
    }
    if (state.status === "saved" && state.at) {
        const time = state.at.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
        return (
            <span className="inline-flex items-center gap-1.5 text-[11px] text-emerald-700">
                <CheckCircle size={12} weight="fill" /> Saved {time}
            </span>
        );
    }
    if (state.status === "error") {
        return (
            <span className="inline-flex items-center gap-1.5 text-[11px] text-rose-700">
                <Warning size={12} weight="fill" /> Save failed
            </span>
        );
    }
    // iter101.56 — Idle state hidden (matches PlanEditorInline).
    return null;
}
